// angular
import { NgOptimizedImage, ViewportScroller } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  OnChanges,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
// components
import { ButtonComponent } from '@app/core/components/button/button.component';
import {
  CardPropertyComponent,
  ICardProperty,
} from '@app/core/components/card-property/card-property.component';
import { CardTitleComponent } from '@app/core/components/card-title/card-title.component';
import { ContactDialogComponent } from '@app/core/dialogs/contact-dialog/contact-dialog.component';
import { GalleryComponent } from '@app/core/components/gallery/gallery.component';
import { SubmitRequestDialogComponent } from '@app/core/dialogs/submit-request-dialog/submit-request-dialog.component';
import { ItemPricesComponent } from '@app/core/components/item-prices/item-prices.component';
// services
import { DialogService } from '@core/shared/services/dialog.service';
import { UserService } from '@app/shared/services/user.service';
import { CurrencyService } from '@app/core/shared/services/currency.service';
import { StorageService } from '@app/shared/services/storage.service';
// types & interfaces & env
import { ContactDialogItem } from '@app/core/dialogs/contact-dialog/shared/contact-dialog-item.type';
import { ProductPreviewType } from '@app/+products/shared/types/product-preview.type';
import { IMedia } from '@app/shared/interfaces/media.interface';
import { ICardTitle } from '@app/shared/interfaces/card-title.interface';
import { PriceType } from '@app/+products/shared/interfaces/product.interface';
import { environment } from '@environments/environment';
// pipes & enums
import { TranslateDataPipe } from '@app/shared/pipes/translate-data.pipe';
import { CurrencyCodes } from '@app/shared/enums/currencies.enum';
import { RegisterButtonComponent } from '@app/core/components/register-button/register-button.component';
import {MatomoTrackClickDirective} from "ngx-matomo-client";

@Component({
  selector: 'pwj-product-preview',
  standalone: true,
  imports: [
    NgOptimizedImage,
    ButtonComponent,
    CardPropertyComponent,
    CardTitleComponent,
    TranslateModule,
    TranslateDataPipe,
    GalleryComponent,
    ItemPricesComponent,
    RegisterButtonComponent,
    MatomoTrackClickDirective,
  ],
  templateUrl: './product-preview.component.html',
  styleUrl: './product-preview.component.scss',
})
export class ProductPreviewComponent implements OnChanges {
  public panelUrl: string = environment.baseUrl.panelUrl;
  public baseUrl: string = environment.baseUrl.base;
  public productProperties!: ICardProperty[];
  public productPreviewTitle!: ICardTitle;
  public selectedProductMedia!: IMedia;
  public contactData!: ContactDialogItem[];
  public userName?: string;
  public currentCurrency: keyof PriceType = CurrencyCodes.usd;
  public isUserLoggedIn!: boolean;

  // inputs
  previewData = input.required<ProductPreviewType>();

  // dependencies
  private readonly _translateService = inject(TranslateService);
  private readonly _dialogService = inject(DialogService);
  private readonly _viewportScroller = inject(ViewportScroller);
  private readonly _userService = inject(UserService);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _currenciesService = inject(CurrencyService);
  private readonly _storageService = inject(StorageService);

  constructor() {
    // set current currency
    this.currentCurrency =
      this._storageService.getCurrency() as keyof PriceType;
    effect(() => {
      this.currentCurrency = this._currenciesService.currentCurrency();
    });

    // checked user logged-in or no
    effect(() => {
      this.isUserLoggedIn =
        (this._userService.userSignal()?.roles?.length || 0) > 0 && !!this._userService.userSignal()?.mobile_verified;
    });

    // get preview data on lang change
    this._translateService.onLangChange.subscribe(_ => {
      this.getPreviewData();
    });

    // set user business id
    effect(() => {
      this.userName = this._userService.userSignal()?.username;
      this._cdr.detectChanges();
    });
  }

  ngOnChanges(): void {
    // check product added to favorite or not
    this.getPreviewData();
  }

  public getPreviewData() {
    if (this.previewData()) {
      this.productPreviewTitle = {
        title: this.previewData()?.title || '-',
        summarizeTitleLength: 50,
        subTitle: this.previewData()?.subTitle || '-',
        summarizeSubTitle: false,
        summarizeSubTitleLength: 90,
        subTitleUrl: this.previewData()?.custom_url
          ? `/${this.previewData()?.custom_url}`
          : null,
        subTitleClasses: '!text-5xl !font-600',
        code: this.previewData()?.code,
        image: {
          src:
            this.previewData()?.logo?.cropped_url?.toLowerCase() ||
            this.previewData()?.logo?.url,
          alt: this.previewData()?.title,
        },
        titleClasses: '!text-11xl !font-600',
        imgClasses: '!w-54 !h-54',
        isVerified: this.previewData()?.businessVerified,
      };

      this.productProperties = [
        {
          name: 'products.productType',
          icon: 'grid-apps',
          value: this.previewData()?.category_type || '-',
          iconClasses: 'text-gray-600',
          isI18nValueType: true,
        },
        {
          name: this._translateService.instant('products.form'),
          icon: '3d-cube',
          value: this.previewData()?.category || '-',
          iconClasses: 'text-gray-600',
          isI18nValueType: true,
        },
        {
          name: this._translateService.instant('products.category'),
          icon: 'grid-apps',
          value: this.previewData()?.sub_category || '-',
          iconClasses: 'text-gray-600',
          isI18nValueType: true,
        },
        {
          name: this._translateService.instant('products.grade'),
          icon: 'ranking-1',
          value: this.previewData()?.stone_grade
            ? `enums.grades.${this.previewData()?.stone_grade}`
            : '-',
          valueClasses: 'text-primary-dark',
          iconClasses: 'text-gray-600',
        },
      ];

      this.selectedProductMedia = this.previewData()?.media[0];

      // set contact data
      this.contactData = [
        {
          id: 1,
          description: 'contactUsDialog.chatOnWhatsApp',
          details: this.previewData()?.mobile,
          detailsClasses: 'is-number block',
          buttonIcon: 'phone',
          buttonText: 'contactUsDialog.whatsApp',
          link:
            this.previewData()?.mobile !== '-'
              ? `https://wa.me/${this.previewData()?.mobile}`
              : undefined,
        },
        {
          id: 2,
          description: 'contactUsDialog.voiceCallWithSupplier',
          details: this.previewData()?.mobile,
          detailsClasses: 'is-number block',
          buttonIcon: 'mobile',
          buttonText: 'contactUsDialog.call',
          link:
            this.previewData()?.mobile !== '-'
              ? `tell:${this.previewData()?.mobile}`
              : undefined,
        },
        {
          id: 3,
          description: 'contactUsDialog.chatWithSupplier',
          buttonIcon: 'message-dots',
          buttonText: 'contactUsDialog.chat',
          needAction: true,
          action: 'openChat',
        },
        {
          id: 4,
          description: 'contactUsDialog.meetingWithSupplier',
          buttonIcon: 'video',
          buttonText: 'contactUsDialog.meeting',
          link: 'https://calendar.google.com/calendar',
        },
      ];
    }
  }

  public onSelectMainImage(media: IMedia) {
    this.selectedProductMedia = media;
  }

  public contactUs() {
    const dialogRef = this._dialogService.open(ContactDialogComponent, {
      width: 'none',
      height: 'none',
      data: {
        title: 'global.contact',
        contactsData: this.contactData,
      },
    });

    dialogRef.afterClosed().subscribe(_ => {});
  }

  public submitARequest() {
    const dialogRef = this._dialogService.open(SubmitRequestDialogComponent, {
      width: 'none',
      height: 'none',
      data: {
        title: 'global.submitARequest',
        productId: this.previewData()?._id,
        productBusinessId: this.previewData()?.business_id,
      },
    });

    dialogRef.afterClosed().subscribe(_ => {});
  }

  public goToDescription() {
    this._viewportScroller.scrollToPosition([0, 800]);
  }
}
